import { useEffect, useState } from "react";
import type { SeasonalThemeId } from "../../shared/themes";
import { isStaleChunk, reloadForNewVersion } from "../ui/ErrorBoundary";
import type { ThemeLook } from "./types";

// Each seasonal theme (shared/themes.ts) keeps its CSS, art and accents in its
// own folder here, loaded only while that theme is on.
const LOOKS: Record<SeasonalThemeId, () => Promise<{ default: ThemeLook }>> = {
  halloween: () => import("./halloween"),
};

/** The theme's look once its chunk has loaded; null while loading or when no theme is on. */
export function useThemeLook(id: SeasonalThemeId | null): ThemeLook | null {
  const [loaded, setLoaded] = useState<{ id: SeasonalThemeId; look: ThemeLook } | null>(null);
  useEffect(() => {
    if (!id || loaded?.id === id) return;
    let live = true;
    LOOKS[id]().then(
      (m) => live && setLoaded({ id, look: m.default }),
      // A theme file from mid-deploy: fetch it fresh and reload; anything else, carry on without the theme.
      (err) => isStaleChunk(err) && reloadForNewVersion(err),
    );
    return () => {
      live = false;
    };
  }, [id, loaded?.id]);
  return id && loaded?.id === id ? loaded.look : null;
}
